import { ReactNode, useEffect, useRef } from 'react';
import { X } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { SetupApiError, SetupStep, setupProgress, customerSetupStatus } from '../../../services/accountSetup/accountSetupService';
import { borderGrey, cardBg, primaryText, secondaryText, greenButtonClass, lightGreenBG, textDarkGreen, redTextColor } from '../../../styling/stylingHelper';
export const setupCard = `rounded-2xl border p-5 sm:p-6 ${borderGrey} ${cardBg}`;
export const setupInput = `min-h-[44px] w-full rounded-lg border px-3 py-2 ${borderGrey} ${cardBg} ${primaryText}`;
export const setupButton = `${greenButtonClass} min-h-[44px] justify-center gap-2 disabled:cursor-not-allowed disabled:opacity-50`;
export const setupSecondary = `inline-flex min-h-[44px] items-center justify-center gap-2 rounded-lg border px-4 py-2 text-sm ${borderGrey} ${primaryText} ${cardBg} disabled:opacity-50`;
export const setupDestructive = `inline-flex min-h-[44px] items-center justify-center gap-2 rounded-lg border border-red-300 px-4 py-2 text-sm dark:border-red-800 ${redTextColor} ${cardBg} disabled:opacity-50`;
export const setupDestructiveConfirm = 'inline-flex min-h-[44px] items-center justify-center gap-2 rounded-lg bg-red-600 px-4 py-2 text-sm font-semibold text-white hover:bg-red-700 disabled:opacity-50 dark:bg-red-700 dark:text-white dark:hover:bg-red-800';
export function setupErrorKey(error: unknown): string {
  if (error instanceof SetupApiError) {
    if (['supplier_not_supported', 'supplier_configuration_required', 'supplier_busy', 'supplier_queue_full', 'supplier_run_interrupted', 'supplier_run_failed', 'supplier_file_failed', 'supplier_client_name_missing', 'supplier_verification_incomplete', 'invalid_section_type', 'invalid_section_name', 'invalid_client_names', 'invalid_order_emails', 'invalid_supplier', 'supplier_not_uploaded', 'section_choice_required', 'section_name_in_use', 'section_not_found', 'client_name_section_conflict', 'supplier_pdf_required', 'supplier_saved_orders_required', 'supplier_reprocess_required', 'file_required', 'supplier_variant_required', 'invalid_supplier_variant', 'supplier_reset_shared_invoice', 'supplier_reset_shared_order', 'supplier_not_needs_attention'].includes(error.code)) return `accountSetup.suppliers.errors.${error.code}`;
    if (error.code === 'demo_restaurants_must_share_demo_owner') return 'accountSetup.admin.demoHelp';
    if (error.code === 'invalid_forwarding_alias') return 'accountSetup.forwarding.invalidAlias';
    if (error.code === 'forwarding_alias_in_use') return 'accountSetup.forwarding.aliasInUse';
    if (error.code === 'invalid_portal_url') return 'accountSetup.error.portalUrl';
    if (['missing_pos_fields', 'invalid_pos_fields', 'invalid_pos_provider', 'password_required', 'invalid_password'].includes(error.code)) return 'accountSetup.error.posFields';
    if (error.code === 'invalid_file_selection') return 'accountSetup.intake.invalidSelection';
    if (error.code === 'supplier_files_processed') return 'accountSetup.intake.processedFiles';
    if (error.code === 'file_limit_reached') return 'accountSetup.error.limit';
    if (error.code === 'replace_or_clear_review_first') return 'accountSetup.error.reviewClosed';
    if (error.status === 409) return 'accountSetup.error.conflict';
    if (error.code === 'file_too_large' || error.status === 413) return 'accountSetup.error.fileSize';
    if (error.status === 403) return 'accountSetup.error.access';
  }
  return 'accountSetup.error.generic';
}
export function SetupError({
  message
}: {
  message: string;
}) {
  const {
    t
  } = useTranslation();
  return message ? <p role="alert" className={`my-3 text-sm ${redTextColor}`}>{t(message)}</p> : null;
}
export function StepStatus({
  step, employee = false
}: {
  step: SetupStep; employee?: boolean;
}) {
  const {
    t
  } = useTranslation();
  const status = customerSetupStatus(step);
  const color = status === 'completed' ? `${lightGreenBG} ${textDarkGreen}` : status === 'pending'
    ? 'bg-orange-50 text-orange-800 dark:bg-orange-950/40 dark:text-orange-300' : `bg-gray-100 dark:bg-gray-800 ${secondaryText}`;
  return <div className="space-y-2"><span className={`inline-flex items-center gap-2 rounded-full px-3 py-1 text-xs ${color}`}><span className="h-1.5 w-1.5 rounded-full bg-current dark:bg-current" />{t(`accountSetup.customerStatus.${status}`)}</span>{status === 'in_progress' && <p className={`text-xs ${secondaryText}`}>{t(employee ? 'accountSetup.admin.waitingCustomer' : 'accountSetup.actionNeeded')}</p>}</div>;
}
export function SetupProgress({
  steps,
  label
}: {
  steps: SetupStep[];
  label: string;
}) {
  const {
    t
  } = useTranslation();
  const value = setupProgress(steps);
  if (value === null) return <span className={`text-xs ${secondaryText}`}>{t('accountSetup.status.not_applicable')}</span>;
  return <div className="flex items-center gap-3"><div role="progressbar" aria-label={label} aria-valuenow={value} aria-valuemin={0} aria-valuemax={100} className={`h-2 flex-1 overflow-hidden rounded-full ${lightGreenBG}`}><div className="h-full rounded-full bg-darkGreenColor dark:bg-darkGreenColor-dark" style={{
        width: `${value}%`
      }} /></div><span className={`text-sm ${secondaryText}`}>{value}%</span></div>;
}

/** Native dialog provides keyboard focus containment and restores the trigger on close. */
export function SetupModal({ title, description, children, onClose, busy = false }: {
  title: string; description?: string; children: ReactNode; onClose: () => void; busy?: boolean;
}) {
  const { t } = useTranslation();
  const dialog = useRef<HTMLDialogElement>(null);
  useEffect(() => {
    const element = dialog.current;
    element?.showModal();
    const previous = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { element?.close(); document.body.style.overflow = previous; };
  }, []);
  return <dialog ref={dialog} aria-label={title} onCancel={event => { event.preventDefault(); if (!busy) onClose(); }}
    className={`m-auto max-h-[90dvh] w-[calc(100%_-_2rem)] max-w-xl overflow-y-auto rounded-2xl border p-0 shadow-xl backdrop:bg-black/50 dark:backdrop:bg-black/70 ${borderGrey} ${cardBg} ${primaryText}`}>
    <div className={`flex items-start justify-between gap-4 border-b p-5 sm:p-6 ${borderGrey}`}>
      <div><h2 className="text-xl font-semibold">{title}</h2>{description && <p className={`mt-2 text-sm leading-6 ${secondaryText}`}>{description}</p>}</div>
      <button type="button" disabled={busy} onClick={onClose} aria-label={t('common.close')} className="flex min-h-[44px] min-w-[44px] items-center justify-center rounded-full disabled:opacity-50 sm:min-h-[40px] sm:min-w-[40px]"><X className="h-5 w-5" /></button>
    </div>
    <div className="p-5 sm:p-6">{children}</div>
  </dialog>;
}
